"use client";

import { formatDistanceToNow } from "date-fns";
import { ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocale, formatNumber } from "@/i18n/format";
import { Badge } from "@/components/ui/badge";

export type TopPiecesSort = "impressions" | "engagement" | "engagementRate";

export interface TopPiece {
  pieceId: string;
  shortCode?: string;
  platform: string;
  publishedAt: string;
  copyPreview: string;
  thumbnailUrl?: string | null;
  impressions: number;
  reach: number;
  engagement: number;
  engagementRate: number;
  /** Null = not reported by the platform (e.g. Instagram Reels) — shown as "—". */
  follows?: string | number | null;
  /** Average watch time / video length, in percent (can exceed 100). */
  pctWatched?: number | null;
}

interface TopPiecesTableProps {
  pieces: TopPiece[];
  sortBy: TopPiecesSort;
  onSortChange: (s: TopPiecesSort) => void;
  onSelectPiece: (pieceId: string) => void;
}

const SORT_CYCLE: Record<TopPiecesSort, TopPiecesSort> = {
  impressions: "engagement",
  engagement: "engagementRate",
  engagementRate: "impressions",
};

function truncate(s: string, n: number) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export function TopPiecesTable({
  pieces,
  sortBy,
  onSortChange,
  onSelectPiece,
}: TopPiecesTableProps) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const cycleSort = () => onSortChange(SORT_CYCLE[sortBy]);

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b p-3">
        <h3 className="text-sm font-medium">{t("topPieces.title")}</h3>
        <button
          type="button"
          onClick={cycleSort}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
        >
          <ArrowUpDown className="h-3 w-3" />
          {t("topPieces.sortLabel", { sortBy })}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="w-12 px-3 py-2 text-left"></th>
              <th className="px-3 py-2 text-left">{t("topPieces.col.copy")}</th>
              <th className="px-3 py-2 text-left">{t("topPieces.col.platform")}</th>
              <th className="px-3 py-2 text-left">{t("topPieces.col.published")}</th>
              <th className="px-3 py-2 text-right">{t("topPieces.col.impressions")}</th>
              <th className="px-3 py-2 text-right">{t("topPieces.col.engagement")}</th>
              <th className="px-3 py-2 text-right">{t("topPieces.col.engRate")}</th>
              <th className="px-3 py-2 text-right">{t("topPieces.col.follows")}</th>
              <th className="px-3 py-2 text-right">{t("topPieces.col.pctWatched")}</th>
            </tr>
          </thead>
          <tbody>
            {pieces.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  {t("topPieces.empty")}
                </td>
              </tr>
            )}
            {pieces.map((p) => (
              <tr
                key={p.pieceId}
                onClick={() => onSelectPiece(p.pieceId)}
                className="cursor-pointer border-t transition hover:bg-muted/60"
              >
                <td className="px-3 py-2">
                  {p.thumbnailUrl ? (
                    <img
                      src={p.thumbnailUrl}
                      alt=""
                      className="h-6 w-6 rounded object-cover"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded bg-muted" />
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="text-foreground">
                    {truncate(p.copyPreview, 60)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <Badge variant="secondary" className="capitalize">
                    {p.platform}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {p.publishedAt
                    ? formatDistanceToNow(new Date(p.publishedAt), {
                        addSuffix: true,
                      })
                    : "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatNumber(p.impressions, locale)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatNumber(p.engagement, locale)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatNumber(p.engagementRate, locale, { style: "percent", maximumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {p.follows != null && p.follows !== ""
                    ? formatNumber(Number(p.follows), locale)
                    : "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {p.pctWatched != null
                    ? `${formatNumber(p.pctWatched, locale, { maximumFractionDigits: 1 })}%`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
